import React, { useState } from 'react';
import { useSiteImages } from '../context/SiteImagesContext';
import { SERVICES_LIST } from '../data/products';
import { useProducts } from '../context/ProductContext';
import { useQuote } from '../context/QuoteContext';
import { SafeImage } from '../components/SafeImage';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { formatKsh } from '../utils/formatters';
import { Wrench, PhoneCall, ShieldCheck, Clock, CheckCircle2, Send, MessageCircle, AlertCircle } from 'lucide-react';

interface ServicesPageProps {
  onNavigate?: (page: string) => void;
}

export const ServicesPage: React.FC<ServicesPageProps> = ({ onNavigate }) => {
  const { images } = useSiteImages();
  const { products } = useProducts();
  const { addToQuote } = useQuote();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [machineType, setMachineType] = useState('Milk ATM');
  const [locationCounty, setLocationCounty] = useState('Nairobi');
  const [issueDescription, setIssueDescription] = useState('');

  const spareProducts = products.filter((p) => p.category === 'spares');

  const handleSubmitService = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'service',
          name: serviceName,
          phone,
          email,
          county: locationCounty,
          notes: `Machine: ${machineType}\n\nService required: ${issueDescription || 'Routine maintenance and inspection'}`,
          items: []
        })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Your service request could not be sent. Please try again.');
      }
      setFormSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Your service request could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppBooking = () => {
    const text = `Hello Saints Tech Solutions! I would like to book an on-site technician service:%0A%0AClient: ${serviceName || 'Client'}%0APhone: ${phone || 'N/A'}%0AMachine: ${machineType}%0ALocation: ${locationCounty}%0AIssue Details: ${issueDescription || 'Routine maintenance and inspection'}`;
    window.open(`https://wa.me/254727072393?text=${text}`, '_blank');
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb="Services"
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={<Wrench className="w-3.5 h-3.5" />}
        badgeText="Dedicated Technical Engineering Department"
        badgeColor="emerald"
        title="Our Technical & Servicing Services"
        description="At Saints Tech Solutions, we ensure uninterrupted machine reliability through scheduled preventative maintenance, emergency on-site technician dispatch, and certified food-grade spare parts across Kenya."
        backgroundImage={images.servicesBanner}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
        actions={
          <>
            <a
              href="tel:+254727072393"
              className="px-5 py-3 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-colors shadow-md"
            >
              <PhoneCall className="w-4 h-4" />
              <span>Call Hotline: +254 727 072 393</span>
            </a>
            <span className="text-xs text-slate-300 font-medium">
              Workshop: Juja Road 478/B Green Mall, Nairobi
            </span>
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

      {/* Services Grid & Maintenance Offerings */}
      <div className="space-y-6">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            Field Support Capabilities
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Specialized Service Packages
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Performed by qualified electromechanical technicians using certified test instruments.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES_LIST.map((srv, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col justify-between space-y-4 shadow-xs hover:border-sky-300 transition-colors"
            >
              <div className="space-y-2.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-sky-700">
                  {srv.category}
                </span>
                <h3 className="text-base font-bold text-slate-900 leading-snug">
                  {srv.title}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {srv.description}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                <div>
                  <span className="text-slate-400 block text-[10px]">Estimated Turnaround</span>
                  <span className="font-semibold text-slate-800">{srv.turnaround}</span>
                </div>
                <div className="text-right">
                  <span className="text-[11px] font-semibold text-sky-700 bg-sky-50 px-2.5 py-1 rounded-md">
                    Certified Support
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Book a Technician On-Site Booking Form */}
      <div className="bg-slate-100 rounded-3xl p-6 sm:p-10 border border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-rose-600">
              Schedule Maintenance
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Request On-Site Technician Dispatch
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              Whether you need urgent flowmeter calibration, Reverse Osmosis membrane restoration, or new shop plumbing connection, our team is on standby to keep your vending kiosk earning.
            </p>

            <div className="space-y-3 pt-2 text-xs text-slate-700">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Rapid response within Nairobi, Kiambu, Machakos & Kajiado</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Regional service routes to Nakuru, Eldoret, Kisumu & Coast</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Original OEM replacement components installed</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
            {formSubmitted ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  Technician Booking Submitted!
                </h3>
                <p className="text-xs text-slate-600 max-w-sm mx-auto">
                  Our dispatch coordinator will contact you at <span className="font-semibold text-slate-800">{phone}</span> shortly to confirm the appointment and technician ETA.
                </p>
                <div className="pt-2 flex justify-center gap-3">
                  <button
                    onClick={handleWhatsAppBooking}
                    className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700"
                  >
                    Open in WhatsApp
                  </button>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="px-4 py-2 text-xs text-slate-600 hover:text-slate-900"
                  >
                    Submit Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitService} className="space-y-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                  Service Request Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Your Name / Business Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={serviceName}
                      onChange={(e) => setServiceName(e.target.value)}
                      placeholder="e.g. Samuel Mutua"
                      className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Direct Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+254 7XX XXX XXX"
                      className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Machine Category
                    </label>
                    <select
                      value={machineType}
                      onChange={(e) => setMachineType(e.target.value)}
                      className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="Milk ATM">Milk ATM Dispenser</option>
                      <option value="Water Purifier RO">Commercial Reverse Osmosis</option>
                      <option value="Water Filling Station">Water Refill Kiosk</option>
                      <option value="Cooking Oil ATM">Cooking Oil ATM</option>
                      <option value="Pasteurizer">3-Phase Pasteurizer Tank</option>
                      <option value="Other">Other Equipment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Location / County
                    </label>
                    <input
                      type="text"
                      value={locationCounty}
                      onChange={(e) => setLocationCounty(e.target.value)}
                      placeholder="e.g. Kasarani, Nairobi"
                      className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Describe the Issue or Service Required
                  </label>
                  <textarea
                    rows={3}
                    value={issueDescription}
                    onChange={(e) => setIssueDescription(e.target.value)}
                    placeholder="e.g. Milk ATM is reading flow incorrectly / RO pressure is climbing above 14 bar / Need annual media sand replacement..."
                    className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                {submitError && (
                  <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {submitError}
                  </p>
                )}

                <div className="pt-2 flex flex-col sm:flex-row gap-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-2.5 px-4 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {isSubmitting ? 'Sending Request...' : 'Book Technician Dispatch'}
                  </button>
                  <button
                    type="button"
                    onClick={handleWhatsAppBooking}
                    className="py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Book via WhatsApp
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Genuine Spare Parts Catalog */}
      <div className="space-y-6">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            Warehouse Spares Inventory
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Original OEM Replacement Parts
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Always in stock at our Nairobi Green Mall warehouse. Order individually or add to quotation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {spareProducts.map((part) => (
            <div
              key={part.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden p-4 flex flex-col justify-between space-y-3 shadow-xs"
            >
              <div className="space-y-2">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-100">
                  <SafeImage
                    src={part.image}
                    fallbackCategory={part.category}
                    alt={part.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xs font-bold text-slate-900 leading-snug line-clamp-2">
                  {part.name}
                </h3>
                <p className="text-[11px] text-slate-500 line-clamp-2">
                  {part.tagline}
                </p>
                <div className="text-sm font-extrabold text-sky-700 tabular-nums">
                  KSh {formatKsh(part.priceKsh)}
                </div>
              </div>

              <button
                onClick={() => addToQuote(part, 1)}
                className="w-full py-2 px-3 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
              >
                Add to Quote
              </button>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};
